import React from 'react';

interface SubtreeRouteOptions {
  /**
   * The subtree's own route hook. Called inside the returned component so the
   * routes are sourced where they are rendered rather than captured by a
   * closure in the parent's route array.
   */
  readonly useRoutes?: () => any;
  /** Prefix the subtree uses to build links, e.g. `customer`. */
  readonly rootPath?: string;
}

/**
 * Binds a subtree component to its own route hook, at module scope.
 *
 * A route's `component` is rendered by React Router as
 * `React.createElement(component, props)`, so the function identity *is* the
 * element type. Writing the boundary as an inline arrow inside a route array
 * hands React a brand-new type on every render of the persona shell: it
 * cannot reconcile the two, so it unmounts the whole subtree and mounts a
 * fresh one. Every piece of state below the boundary is destroyed with it —
 * which is how a half-filled form silently reset itself (FIR-21142).
 *
 * Calling this at module scope gives the boundary one stable type for the life
 * of the tab. `React.useMemo` around the route array looks like it fixes the
 * same thing, but it only holds while the memo's dependencies do — and React
 * treats a memo as a discardable performance hint, not a guarantee. Mount
 * identity is not something to leave to a cache.
 */
export const createSubtreeRoute = (
  Subtree: React.ComponentType<any>,
  { useRoutes, rootPath }: SubtreeRouteOptions = {}
) => {
  const staticProps = rootPath ? { rootPath } : {};

  // Branching here rather than inside the component keeps the hook call
  // unconditional for whichever variant is built.
  const SubtreeRoute: React.FC<any> = useRoutes
    ? (props: any) => {
        const routes = useRoutes();
        return <Subtree routes={routes} {...staticProps} {...props} />;
      }
    : (props: any) => <Subtree {...staticProps} {...props} />;

  SubtreeRoute.displayName = `SubtreeRoute(${
    Subtree.displayName || Subtree.name || 'Component'
  })`;

  return SubtreeRoute;
};
